import { api } from '../api'
import {
  BarChart,
  CHART_COLORS,
  Empty,
  Panel,
  StatTile,
  StateBadge,
  Table,
  TileRow,
  Page,
  fmtDuration,
  fmtNumber,
  fmtPhone,
  fmtTime,
  usePoll,
} from '../components/ui'
import type { ActiveCall, HourlyPoint, LiveBoard, Overview, QueueDepth } from '../types'

export default function Dashboard({ onNavigate }: { onNavigate: (r: 'campaigns' | 'leads' | 'calls') => void }) {
  // Live figures refresh fast because they are read from memory; historical
  // aggregates refresh slowly because they are not.
  const live = usePoll<LiveBoard>(() => api.liveAgents(), 3000)
  const calls = usePoll<ActiveCall[]>(() => api.activeCalls(), 3000)
  const stats = usePoll<Overview>(() => api.overview(), 15000)
  const hourly = usePoll<HourlyPoint[]>(() => api.hourly(), 60000)
  const queues = usePoll<QueueDepth[]>(() => api.queues(), 15000)

  const s = stats.data
  const abandonTone =
    !s ? undefined : s.abandon_rate_30d >= 3 ? 'danger' : s.abandon_rate_30d >= 2.5 ? 'warn' : 'ok'

  return (
    <Page title="Dashboard" subtitle="Today at a glance">
      <TileRow>
        <StatTile label="Attempts" value={s ? fmtNumber(s.attempts) : '—'} sub="calls placed today" />
        <StatTile
          label="Connects"
          value={s ? fmtNumber(s.connects) : '—'}
          sub={s ? `${s.connect_rate}% of attempts` : undefined}
          tone="live"
        />
        <StatTile
          label="Contacts"
          value={s ? fmtNumber(s.contacts) : '—'}
          sub={s ? `${s.contact_rate}% of connects` : undefined}
        />
        <StatTile
          label="Sales"
          value={s ? fmtNumber(s.sales) : '—'}
          sub={s ? `${s.conversion_rate}% conversion` : undefined}
          tone="ok"
        />
        <StatTile
          label="Abandon rate"
          value={s ? `${s.abandon_rate_30d}%` : '—'}
          sub="rolling 30 days · 3% ceiling"
          tone={abandonTone as never}
        />
        <StatTile
          label="Avg talk"
          value={s ? fmtDuration(s.avg_talk_sec) : '—'}
          sub={s ? `${fmtDuration(s.avg_ring_sec)} to answer` : undefined}
        />
      </TileRow>

      <div className="dash-grid">
        <Panel title="Activity by hour" className="span-2">
          <BarChart
            data={(hourly.data ?? []).map((h) => ({
              label: fmtTime(h.hour),
              values: [
                { value: h.attempts, color: CHART_COLORS.attempts, name: 'Attempts' },
                { value: h.answered, color: CHART_COLORS.answered, name: 'Answered' },
                { value: h.connects, color: CHART_COLORS.connects, name: 'Connects' },
                { value: h.contacts, color: CHART_COLORS.contacts, name: 'Contacts' },
              ],
            }))}
          />
        </Panel>

        <Panel
          title="Agents"
          actions={
            <span className="muted small">
              {live.data ? `${live.data.summary.online} online` : ''}
            </span>
          }
        >
          {!live.data || live.data.agents.length === 0 ? (
            <Empty message="No agents online." hint="They appear here as soon as they go online in the Dialer." />
          ) : (
            <>
              <div className="mini-stats">
                <span><b>{live.data.summary.ready}</b> ready</span>
                <span><b>{live.data.summary.talking}</b> talking</span>
                <span><b>{live.data.summary.wrap}</b> wrap</span>
                <span><b>{live.data.summary.paused}</b> paused</span>
              </div>
              <ul className="agent-list">
                {live.data.agents.map((a) => (
                  <li key={a.session_id}>
                    <span className="agent-name">{a.full_name}</span>
                    <span className="muted small">{a.campaign ?? '—'}</span>
                    <StateBadge state={a.state} />
                  </li>
                ))}
              </ul>
            </>
          )}
        </Panel>

        <Panel
          title="Live calls"
          actions={<button className="link" onClick={() => onNavigate('calls')}>Call log →</button>}
        >
          {!calls.data || calls.data.length === 0 ? (
            <Empty message="Nothing on the wire right now." />
          ) : (
            <Table head={['Number', 'Campaign', 'Agent', 'State']}>
              {calls.data.map((c) => (
                <tr key={c.id}>
                  <td className="mono">{fmtPhone(c.number)}</td>
                  <td>{c.campaign ?? '—'}</td>
                  <td>{c.agent ?? '—'}</td>
                  <td><StateBadge state={c.state} /></td>
                </tr>
              ))}
            </Table>
          )}
        </Panel>

        <Panel
          title="Queue depth"
          actions={<button className="link" onClick={() => onNavigate('leads')}>Leads →</button>}
        >
          {!queues.data || queues.data.length === 0 ? (
            <Empty
              message="No active campaigns."
              hint="Create one under Campaigns, then load leads into a list."
            />
          ) : (
            <Table head={['Campaign', 'Dialable', 'Callbacks', 'Total']}>
              {queues.data.map((q) => (
                <tr key={q.campaign_id}>
                  <td>{q.campaign}</td>
                  <td className={q.dialable === 0 ? 'danger-text' : ''}>{fmtNumber(q.dialable)}</td>
                  <td>{fmtNumber(q.callbacks_due)}</td>
                  <td className="muted">{fmtNumber(q.total)}</td>
                </tr>
              ))}
            </Table>
          )}
        </Panel>
      </div>
    </Page>
  )
}
